import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Eye, EyeOff, Sparkles, Smartphone } from "lucide-react";

type Mode = "login" | "signup";

export default function Auth() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPairForm, setShowPairForm] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [pairLoading, setPairLoading] = useState(false);

  // Detect iOS standalone PWA, where Google OAuth can bounce out to Safari and lose the session.
  const isIosStandalonePwa = (() => {
    if (typeof window === "undefined") return false;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // @ts-ignore — iOS-only property
      window.navigator.standalone === true;
    const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
    return standalone && isIos;
  })();

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/auth`,
      });
      if (error) throw error;
    } catch (err: any) {
      toast({ title: "Google sign-in failed", description: err.message, variant: "destructive" });
      setGoogleLoading(false);
    }
  };

  const handleRedeemPairing = async (e: React.FormEvent) => {
    e.preventDefault();
    setPairLoading(true);
    try {
      const normalized = pairCode.trim().toUpperCase().replace(/\s+/g, "");
      const { data, error } = await supabase.functions.invoke("redeem-pairing-code", {
        body: { code: normalized },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      const { token_hash, email: userEmail } = data as { token_hash: string; email: string };
      const { error: verifyErr } = await supabase.auth.verifyOtp({
        token_hash,
        type: "magiclink",
      });
      if (verifyErr) throw verifyErr;
      // onAuthStateChange listener handles navigation
    } catch (err: any) {
      toast({ title: "Pairing failed", description: err.message, variant: "destructive" });
    } finally {
      setPairLoading(false);
    }
  };

  useEffect(() => {
    // Check existing session on mount (catches returning OAuth redirects)
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate("/dashboard", { replace: true });
    });

    // Also listen for auth state changes (fires when OAuth redirect completes)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) navigate("/dashboard", { replace: true });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        toast({
          title: "Account created!",
          description: "Check your email to confirm your account, then sign in.",
        });
        setMode("login");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img src="/images/mindmail-logo.png" alt="MindMail" className="h-12 w-auto" />
        </div>

        <div className="cosmic-card p-8 rounded-2xl">
          <div className="flex items-center gap-2 mb-6">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="cosmic-button w-full" disabled={loading}>
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Google */}
          <Button
            type="button"
            variant="outline"
            className="w-full flex items-center gap-3"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
          >
            {/* Google logo SVG */}
            <svg className="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {googleLoading ? "Redirecting…" : "Continue with Google"}
          </Button>

          {/* Pairing code (great for iPhone PWA) */}
          <div className="mt-3">
            {!showPairForm ? (
              <button
                type="button"
                onClick={() => setShowPairForm(true)}
                className="w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground py-2"
              >
                <Smartphone className="h-4 w-4" />
                I have a pairing code
              </button>
            ) : (
              <form onSubmit={handleRedeemPairing} className="space-y-2 pt-2">
                <Label htmlFor="pair-code" className="text-xs">Pairing code</Label>
                <Input
                  id="pair-code"
                  value={pairCode}
                  onChange={(e) => setPairCode(e.target.value.toUpperCase())}
                  placeholder="e.g. 7K2P9X"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  className="text-base tracking-[0.3em] text-center font-mono uppercase"
                  maxLength={12}
                  required
                />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setShowPairForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="cosmic-button flex-1" disabled={pairLoading}>
                    {pairLoading ? "Signing in…" : "Sign in"}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground text-center pt-1">
                  Generate one on the desktop site under your account menu.
                </p>
              </form>
            )}
          </div>

          {isIosStandalonePwa && !showPairForm && (
            <p className="mt-3 text-center text-xs text-muted-foreground leading-relaxed">
              On iPhone home-screen apps, Google sign-in opens Safari. Use a pairing code from
              the desktop site for a one-tap sign-in instead.
            </p>
          )}

          <p className="mt-5 text-center text-sm text-muted-foreground">
            {mode === "login" ? "Don't have an account? " : "Already have an account? "}
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="text-primary hover:underline font-medium"
            >
              {mode === "login" ? "Sign up" : "Sign in"}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
